import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { SERVER_URL } from "../../../constants.js";
import { jsonOr } from "../../../utils/http";

export default function useTransportGroupsByDate(selectedDate) {
  const [transportGroups, setTransportGroups] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dateStr = useMemo(
    () => (selectedDate ? selectedDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")),
    [selectedDate]
  );

  const token = () => sessionStorage.getItem("bearer");

  const fetchGroups = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}api/transport/groups?date=${dateStr}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) throw new Error("Failed to fetch transport groups");
      const data = await res.json();

      setTransportGroups(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      console.error("[useTransportGroupsByDate] fetchGroups failed:", e);
      setError(e);
      setTransportGroups([]);
      return [];
    }
  }, [dateStr]);

  const fetchStatuses = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}api/group-statuses?type=TRANSPORT`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const data = await jsonOr(res, []);
      setAvailableStatuses(Array.isArray(data) ? data : []);
      return data;
    } catch (e) {
      console.error("[useTransportGroupsByDate] fetchStatuses failed:", e);
      setError(e);
      setAvailableStatuses([]);
      return [];
    }
  }, []);

  const saveGroupChanges = useCallback(
    async (groupChanges) => {
      if (!groupChanges?.length) return { ok: true };

      setLoading(true);
      setError(null);

      // ✅ This is the day you're planning for (e.g. 2026-02-01)
      const effectiveDateStr =
        selectedDate?.format?.("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD");

      const addBody = JSON.stringify({
        start_date: effectiveDateStr,
        end_date: effectiveDateStr,
      });

      const endBody = JSON.stringify({
        end_date: effectiveDateStr,
      });

      const requests = groupChanges.flatMap((c) => {
        const ops = [];

        const personType = String(c.personType || "").toUpperCase();
        const isAdult =
          personType === "ADULT" || (c.adultId && Number(c.adultId) !== 0);

          const personId = isAdult ? Number(c.adultId) : Number(c.studentId);
          const segment = isAdult ? "adult" : "student";

          const previousGroup = c.previousGroup || null;
          const newGroup = c.newGroup || null;

          // ✅ no-op guard
          if (String(previousGroup ?? "") === String(newGroup ?? "")) return ops;

          // ✅ Removing for this day: end the assignment on this day (don’t delete history)
          if (previousGroup && previousGroup !== newGroup) {
            ops.push(
              fetch(
                `${SERVER_URL}api/campgroup/${previousGroup}/${segment}/${personId}/end`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token()}`,
                  },
                  body: endBody,
                }
              )
            );
          }

        // ✅ Adding for this day: send start/end in body so backend doesn't use "today"
        if (newGroup) {
          ops.push(
            fetch(`${SERVER_URL}api/campgroup/${newGroup}/${segment}/${personId}`, {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token()}`,
              },
              body: addBody,
            })
          );
        }

        return ops;
      });

      try {
        const res = await Promise.all(requests);
        const allOk = res.every((r) => r.ok);
        await fetchGroups();
        return { ok: allOk };
      } catch (e) {
        console.error("[useTransportGroupsByDate] saveGroupChanges failed:", e);
        setError(e);
        return { ok: false, error: e };
      } finally {
        setLoading(false);
      }
    },
    [fetchGroups, selectedDate]
  );

  const updateStops = useCallback(async (groupId, stops) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}api/transport/groups/${groupId}/stops`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(stops || []),
      });

      if (!res.ok) throw new Error("Failed to update stops");
      await fetchGroups();
      return { ok: true };
    } catch (e) {
      console.error("[useTransportGroupsByDate] updateStops failed:", e);
      setError(e);
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [fetchGroups]);


  const createAndScheduleGroup = useCallback(async (groupData) => {
    setLoading(true);
    setError(null);

    const normaliseTime = (t) => {
      if (!t) return null;
      const s = String(t).trim();
      if (!s) return null;
      if (/^\d{2}:\d{2}$/.test(s)) return s;        // backend parseTime handles HH:mm
      if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;  // also fine
      return null;
    };

    const stops = Array.isArray(groupData?.stops)
      ? groupData.stops.map((s, i) => ({
          stopOrder: Number(s.stopOrder ?? (i + 1)),
          location: s.location,
          campusId: s.campusId ?? null,
          plannedTime: normaliseTime(s.plannedTime),
          stopType: s.stopType || (i === 0 ? "PICKUP" : "DROPOFF"),
        }))
      : [];

    const payload = {
      campDate: dateStr,
      groupName: String(groupData.groupName || "").trim(),
      capacity: Number(groupData.capacity),
      status: "Planned",
      meta: {
        journeyType: String(groupData.journeyType || "").trim().toUpperCase(), // "ARRIVALS" | "DEPARTURES"
        provider: groupData.provider ?? null, // provider lives in meta (matches Transport plan)
      },
      stops,
    };

    try {
      const res = await fetch(`${SERVER_URL}api/transport/groups`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to create transport group");

      const created = await res.json(); // TransportGroupDTO
      await fetchGroups();

      return { ok: true, scheduled: true, group: created };
    } catch (e) {
      console.error("[useTransportGroupsByDate] createAndScheduleGroup failed:", e);
      setError(e);
      return { ok: false, error: e };
    } finally {
      setLoading(false);
    }
  }, [dateStr, fetchGroups]);

  const updateGroupStatus = useCallback(async (groupId, newStatus) => {
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}api/campgroup/${groupId}/status`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) return { ok: false };
      await fetchGroups();
      return { ok: true };
    } catch (e) {
      console.error("[useTransportGroupsByDate] updateGroupStatus failed:", e);
      setError(e);
      return { ok: false, error: e };
    }
  }, [fetchGroups]);

  // Refetch when date changes
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Load statuses once
  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const updateTransportGroupBasics = useCallback(async (groupId, patch, existingGroup) => {
    const campus = existingGroup?.campus ?? ""; // preserve
    const res = await fetch(`${SERVER_URL}api/campgroup/${groupId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        groupName: patch.groupName,
        capacity: Number(patch.capacity),
        notes: patch.notes ?? "",
        campus,                 // ✅ preserve
        groupType: "TRANSPORT", // ✅ preserve
      }),
    });

    return { ok: res.ok, status: res.status };
  }, []);

  const updateTransportGroupMeta = useCallback(async (groupId, patch) => {
    const journeyType = String(patch?.journeyType || "").trim().toUpperCase();
    const hasProvider = patch?.provider !== undefined;
    if (!journeyType && !hasProvider) return { ok: true, skipped: true };

    // provider lives in group meta (same as journeyType), so it updates via
    // the meta endpoint — not the generic campgroup basics PUT.
    const body = {};
    if (journeyType) body.journeyType = journeyType;
    if (hasProvider) body.provider = patch.provider ?? null;

    const res = await fetch(`${SERVER_URL}api/transport/groups/${groupId}/meta`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    return { ok: res.ok, status: res.status };
  }, []);

  const updateTransportGroup = useCallback(
    async (groupId, patch, existingGroup) => {
      setLoading(true);
      setError(null);

      try {
        // 1) basics
        const basicsRes = await updateTransportGroupBasics(groupId, patch, existingGroup);
        if (!basicsRes.ok) {
          await fetchGroups();
          return { ok: false, basicsStatus: basicsRes.status };
        }

        // ✅ 2) meta (journeyType)
        const metaRes = await updateTransportGroupMeta(groupId, patch);
        if (!metaRes.ok) {
          await fetchGroups();
          return { ok: false, metaStatus: metaRes.status };
        }

        // 3) stops
        const stopsRes = await updateStops(groupId, patch.stops || []);
        if (!stopsRes.ok) {
          return { ok: false, stopsStatus: stopsRes.error?.status || 500 };
        }

        await fetchGroups();
        return { ok: true, basicsStatus: basicsRes.status };
      } catch (e) {
        console.error("[useTransportGroupsByDate] updateTransportGroup failed:", e);
        setError(e);
        return { ok: false, error: e };
      } finally {
        setLoading(false);
      }
    },
    [fetchGroups, updateStops, updateTransportGroupBasics, updateTransportGroupMeta]
  );

  // Unassign a list of passengers from a specific group for the selected day
  // passengers = array of transfer rows (same objects you render in TransfersTable)
  const unassignPassengersFromGroup = useCallback(
    async (groupId, passengers) => {
      if (!groupId || !Array.isArray(passengers) || passengers.length === 0) {
        return { ok: true, removed: 0 };
      }

      const changes = passengers.map((t) => {
        const personType = String(t?.personType || "").toUpperCase();
        const isAdult = personType === "ADULT" || (t?.adultId && Number(t.adultId) !== 0);

        return {
          personKey: isAdult ? `A:${t.adultId}` : `S:${t.studentId}`,
          personType: isAdult ? "ADULT" : "STUDENT",
          studentId: isAdult ? null : t.studentId,
          adultId: isAdult ? t.adultId : null,
          previousGroup: groupId,
          newGroup: null,
        };
      });

      const res = await saveGroupChanges(changes);
      return { ...res, removed: passengers.length };
    },
    [saveGroupChanges]
  );

  // Confirm a transport group via the dedicated endpoint (requires contact and
  // stamps confirmedBy/At), keeping confirmation consistent with the Transport
  // board. Surfaces CONTACT_REQUIRED so the caller can collect contact details.
  const confirmTransportGroup = useCallback(
    async (groupId) => {
      setError(null);
      try {
        const res = await fetch(`${SERVER_URL}api/transport/groups/${groupId}/confirm`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) {
          return {
            ok: false,
            status: res.status,
            contactRequired: res.headers.get("X-Error") === "CONTACT_REQUIRED",
          };
        }
        await fetchGroups();
        return { ok: true };
      } catch (e) {
        console.error("[useTransportGroupsByDate] confirmTransportGroup failed:", e);
        setError(e);
        return { ok: false, error: e };
      }
    },
    [fetchGroups]
  );

  // Unconfirm (reverts to Planned and clears the confirmed stamp) so moving away
  // from Confirmed via the status control doesn't leave a stale confirmation.
  const unconfirmTransportGroup = useCallback(
    async (groupId) => {
      setError(null);
      try {
        const res = await fetch(`${SERVER_URL}api/transport/groups/${groupId}/unconfirm`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) return { ok: false, status: res.status };
        await fetchGroups();
        return { ok: true };
      } catch (e) {
        console.error("[useTransportGroupsByDate] unconfirmTransportGroup failed:", e);
        setError(e);
        return { ok: false, error: e };
      }
    },
    [fetchGroups]
  );

  // Persist just the contact fields. The backend meta upsert uses
  // COALESCE(VALUES(col), col), so sending only contact leaves journeyType,
  // provider, etc. untouched.
  const saveTransportGroupContact = useCallback(async (groupId, contactName, contactNumber) => {
    setError(null);
    try {
      const res = await fetch(`${SERVER_URL}api/transport/groups/${groupId}/meta`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contactName: String(contactName || "").trim(),
          contactNumber: String(contactNumber || "").trim(),
          clearFields: [],
        }),
      });
      return { ok: res.ok, status: res.status };
    } catch (e) {
      console.error("[useTransportGroupsByDate] saveTransportGroupContact failed:", e);
      setError(e);
      return { ok: false, error: e };
    }
  }, []);

  return {
    transportGroups,
    availableStatuses,
    loading,
    error,
    saveGroupChanges,
    createAndScheduleGroup,
    updateGroupStatus,
    updateStops,
    refetchTransportGroups: fetchGroups,
    updateTransportGroup,
    updateTransportGroupBasics,
    unassignPassengersFromGroup,
    updateTransportGroupMeta,
    confirmTransportGroup,
    unconfirmTransportGroup,
    saveTransportGroupContact,
  };
}
